import { Alert, Button, FormControl, FormControlLabel, FormLabel, Grid2, Radio, RadioGroup, Slider, Typography } from '@mui/material';
import { BookingOrigin, PaymentPlatform } from '@utriper/core';
import { CurrencyInput, LookUpCost, SelectComponent } from '@utriper/core-ui';
import { Method } from 'core/api/fetch';
import { EntityService } from 'core/api/services/lookUp';
import React, { useEffect, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const CostForm = ({ id, initialCost, entity = 'experiencer', entityId, loading = false, fetchMethod = Method.POST, onClose = () => {}, updateCost = () => {}, postCost = () => {}, showPaymentPlatform = false, showOrigin = false }) => {
    const { t: translate, i18n } = useTranslation();
    const [canEdit, setCanEdit] = useState(false);

    const typeValuesOptions = ['inherit', 'percentage', 'unit'];

    const paymentPlatformOptions = useMemo(
        () => [
            { name: '', id: '' },
            ...PaymentPlatform?.getIds().map((id) => {
                const translated = PaymentPlatform.getTranslatedName(PaymentPlatform.types, id, i18n.language);
                return { name: translated?.[0] ?? '', id };
            }),
        ],
        [i18n.language]
    );

    const marks = [
        {
            value: 0,
            label: '0%',
        },
        {
            value: 100,
            label: '100%',
        },
    ];

    const getInitialValues = (cost) => ({
        ...cost,
        paymentPlatformId: cost?.paymentPlatformId || '',
        typeValue: cost?.percentage ? 'percentage' : cost?.currency ? 'unit' : 'inherit',
    });

    const onSubmit = async (values) => {
        try {
            const { costId, typeValue, percentage, price, currency, paymentPlatformId } = values;
            if (!costId) {
                throw new Error(translate('edit_cost.form.errors.cost_id'));
            }
            if (typeValue !== 'inherit' && !percentage && !price && !currency) {
                throw new Error(translate('edit_cost.form.errors.type_value'));
            }
            if (typeValue === 'percentage' && !percentage) {
                throw new Error(translate('edit_cost.form.errors.percentage'));
            }

            if (typeValue === 'unit' && !price && !currency) {
                throw new Error(translate('edit_cost.form.errors.fixed_value'));
            }

            const valuesClon = { ...values };

            valuesClon[`${entity}Id`] = entityId;

            if (!valuesClon?.[`${entity}Id`]) {
                throw new Error(`${entity} ${translate('edit_cost.form.errors.entity')}`);
            }

            if (typeValue === 'unit' && !currency) {
                valuesClon.currency = 'USD';
            }

            if (typeValue === 'percentage') {
                valuesClon.currency = null;
                valuesClon.price = null;
            } else if (typeValue === 'unit') {
                valuesClon.percentage = null;
            } else {
                valuesClon.percentage = null;
                valuesClon.price = null;
                valuesClon.currency = null;
            }

            if (showPaymentPlatform) {
                valuesClon.paymentPlatformId = paymentPlatformId || null;
            }

            if (fetchMethod === Method.POST) {
                const newCost = await postCost(valuesClon);
                if (newCost?.id) {
                    onClose(true);
                    toast.success(translate('edit_cost.form.success.new'), {
                        position: 'top-right',
                        theme: 'colored',
                    });
                } else {
                    onClose();
                    throw new Error(translate('edit_cost.form.errors.new'));
                }
            } else if (fetchMethod === Method.PATCH && initialCost?.costId) {
                const result = await updateCost(id, valuesClon);
                if (result) {
                    onClose(true);
                    toast.success(translate('edit_cost.form.success.update'), {
                        position: 'top-right',
                        theme: 'colored',
                    });
                } else {
                    onClose();
                    throw new Error(translate('edit_cost.form.errors.update'));
                }
            }
        } catch (error) {
            toast.error(error?.message || error, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    };

    useEffect(() => {
        const validEntities = ['experience', 'experiencer', 'contract'];
        if (!validEntities.includes(entity) || !entity || !entityId) {
            toast.error(`Invalid entity: "${entity}"`, {
                position: 'top-right',
                theme: 'colored',
            });
            onClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entity, entityId]);
    
    const excludeBookingOriginIds = new Set([BookingOrigin.IDS.undefined, BookingOrigin.IDS.webapp, BookingOrigin.IDS.mobileapp, BookingOrigin.IDS.code]);

    const originOptions = useMemo(() => {
        const ids = BookingOrigin.getIds();

        const filteredIds = ids.filter((id) => !excludeBookingOriginIds.has(id));

        return filteredIds.map((id) => {
            const translated = BookingOrigin.getTranslatedName(BookingOrigin.types, id, i18n.language);
            return { name: translated?.[0] ?? '', id };
        });
    }, [BookingOrigin, i18n.language]);

    return (
        <Form
            key={`edit-cost-${id}`}
            onSubmit={onSubmit}
            initialValues={getInitialValues(initialCost)}
            render={({ values, handleSubmit, form }) => {
                const handleChangeTypeValue = (event) => {
                    form.change('typeValue', event?.target?.value);
                    setCanEdit(true);
                };

                const handleChangeCurrency = (newCurrency) => {
                    form.change('currency', newCurrency);
                    setCanEdit(true);
                };

                return (
                    <form key={`edit-cost-${id}`} onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <Grid2 container direction="column" width="100%" p={2} spacing={1}>
                            <Grid2 width="100%">
                                <Field
                                    id="costId"
                                    name="costId"
                                    render={({ input, meta, ...rest }) => (
                                        <LookUpCost
                                            {...rest}
                                            label={`${translate('edit_cost.form.cost_label')} *`}
                                            value={values?.costId}
                                            entityService={EntityService}
                                            onChange={(newValue) => {
                                                input.onChange(newValue);
                                                setCanEdit(true);
                                            }}
                                        />
                                    )}
                                />
                            </Grid2>

                            {showOrigin && (
                                <Grid2 container width="100%" my={2}>
                                    <FormControl fullWidth>
                                        <Field
                                            id="originId"
                                            name="originId"
                                            render={({ input }) => (
                                                <SelectComponent
                                                    {...input}
                                                    values={originOptions}
                                                    value={input?.originId}
                                                    label="Origin"
                                                    labelId="origin-label"
                                                    style={{ width: '100%' }}
                                                    onFieldChange={(event) => {
                                                        const value = event.target.value;
                                                        input.onChange(value);
                                                        setCanEdit(true);
                                                    }}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Grid2>
                            )}

                            {showPaymentPlatform && (
                                <Grid2 width="100%" my={2}>
                                    <Field
                                        id="paymentPlatformId"
                                        name="paymentPlatformId"
                                        render={({ input, meta, ...rest }) => (
                                            <SelectComponent
                                                {...rest}
                                                label={translate('edit_cost.form.payment_platform')}
                                                values={paymentPlatformOptions}
                                                style={{ width: '100%' }}
                                                value={input.value}
                                                onFieldChange={(e) => {
                                                    input.onChange(e.target.value);
                                                    setCanEdit(true);
                                                }}
                                            />
                                        )}
                                    />
                                </Grid2>
                            )}
                            <Grid2 container width="100%" my={2}>
                                <FormControl fullWidth>
                                    <FormLabel id="type-value-radio-buttons-group-label">{translate('edit_cost.form.type_value')}</FormLabel>
                                    <RadioGroup row aria-labelledby="type-value-radio-buttons-group-label" name="type-value-radio-buttons-group" value={values.typeValue} onChange={handleChangeTypeValue}>
                                        {typeValuesOptions.map((typeValue) => (
                                            <FormControlLabel key={typeValue} value={typeValue} control={<Radio />} label={typeValue === 'percentage' ? translate('edit_cost.form.percentage') : typeValue === 'unit' ? translate('edit_cost.form.fixed_price') : translate('edit_cost.form.inherit_label')} />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            </Grid2>
                            <Grid2 container width="100%" my={2}>
                                {values?.typeValue === 'percentage' ? (
                                    <>
                                        <Typography id="percentage-slider">{`${translate('edit_cost.form.percentage')}: ${values?.percentage}%`}</Typography>
                                        <Field
                                            id="percentage"
                                            name="percentage"
                                            aria-labelledby="percentage-slider"
                                            marks={marks}
                                            min={0}
                                            step={1}
                                            max={100}
                                            render={({ input, meta, ...rest }) => (
                                                <Slider
                                                    {...rest}
                                                    value={typeof input.value === 'number' ? input.value : 0}
                                                    onChange={(e, newValue) => {
                                                        input.onChange(newValue);
                                                        setCanEdit(true);
                                                    }}
                                                    aria-labelledby="percentage-slider"
                                                />
                                            )}
                                        />
                                        <Alert sx={{ width: '100%', marginTop: 2 }} severity="info">
                                            {translate('edit_cost.form.alerts.value')}
                                        </Alert>
                                    </>
                                ) : values?.typeValue === 'unit' ? (
                                    <>
                                        <Field
                                            id="price"
                                            name="price"
                                            label={translate('edit_cost.form.fixed_price')}
                                            currency={values.currency}
                                            onCurrencyChange={handleChangeCurrency}
                                            render={({ input, meta, ...rest }) => (
                                                <CurrencyInput
                                                    {...rest}
                                                    value={typeof input.value === 'number' ? input.value : 0}
                                                    onChange={(newValue) => {
                                                        input.onChange(newValue);
                                                        setCanEdit(true);
                                                    }}
                                                />
                                            )}
                                        />
                                        <Alert sx={{ width: '100%', marginTop: 2 }} severity="info">
                                            {translate('edit_cost.form.alerts.value')}
                                        </Alert>
                                    </>
                                ) : (
                                    <Alert sx={{ width: '100%' }}>{translate('edit_cost.form.alerts.inherit')}</Alert>
                                )}
                            </Grid2>
                            <Grid2 container justifyContent="flex-end">
                                <Grid2>
                                    <Button
                                        onClick={(e) => {
                                            onClose();
                                            return form.reset(e);
                                        }}
                                        sx={{ color: '#363740 !important', fontWeight: 'bold' }}
                                        variant="outlined">
                                        {translate('btn_cancel')}
                                    </Button>
                                </Grid2>
                                <Grid2>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        loading={loading}
                                        sx={{ color: 'white !important', fontWeight: 'bold', backgroundColor: '#137DC5' }}
                                        disabled={!canEdit}
                                        onClick={(e) => {
                                            return handleSubmit(e);
                                        }}>
                                        {translate('btn_add')}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Grid2>
                    </form>
                );
            }}
        />
    );
};

export default CostForm;
